import React, { useState, useEffect } from 'react';
import T from '../../../components/common/T';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axiosConfig';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../context/AuthContext';
import RoleMatrix from '../../../components/rbac/RoleMatrix';

const AddRole = () => {
  const navigate = useNavigate();
  const { checkAccess } = useAuth();

  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Basic Access Check
    if (checkAccess && !checkAccess('role.create')) {
        toast.error("Access Denied");
        navigate('/admin/roles');
    }
  }, [checkAccess, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) return toast.error("Role Name is required");
    if (selectedPermissions.length === 0) return toast.warn("Please enable at least one permission");

    setLoading(true);
    try {
      // CALL BACKEND
      await api.post('/roles', {
        name: roleName,
        description: description,
        permissions: selectedPermissions 
      });

      toast.success("Role created successfully!");
      setTimeout(() => navigate('/admin/roles'), 1000);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      
      <div className="max-w-6xl mx-auto mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
          <ShieldCheckIcon className="w-8 h-8 text-emerald-600" />
          Create New Role
        </h2>
        <p className="text-gray-500 mt-1 ml-11"><T>Define access levels and permissions for system users.</T></p>
      </div>

      <div className="max-w-6xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-base font-bold text-gray-800 mb-4 border-b pb-2"><T>Role Details</T></h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2"><T>Role Name</T></label>
                <input 
                  type="text" 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Site Manager"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2"><T>Description</T></label>
                <input 
                  type="text" 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Full access to inventory..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-end mb-4">
              <h3 className="text-base font-bold text-gray-800"><T>Permission Settings</T></h3>
              <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full">
                {selectedPermissions.length} Enabled
              </span>
            </div>
            
            <RoleMatrix 
              selectedPermissions={selectedPermissions}
              setSelectedPermissions={setSelectedPermissions}
            />
          </div>

          <div className="flex justify-end gap-4 pt-8 border-t border-gray-200">
            <button 
              type="button"
              onClick={() => navigate('/admin/roles')}
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700 shadow-lg"
            >
              {loading ? 'Saving...' : 'Save Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRole;